/**
 * Saisies d'inventaire gardées sur l'appareil tant que la base ne les a pas confirmées.
 *
 * L'inventaire se fait souvent sans réseau (réserve, chambre froide) : chaque quantité tapée
 * est d'abord écrite ici, puis envoyée en arrière-plan. Elle n'est retirée qu'une fois
 * l'écriture confirmée, ce qui la fait survivre à un rechargement de la page, à la fermeture
 * du navigateur ou au redémarrage de l'appareil. Une saisie n'est ainsi jamais à recompter.
 *
 * Stockage : une entrée localStorage par inventaire, `{ [item_id]: PendingCount }`.
 *
 * Chaque saisie reçoit une version unique (`rev`), transmise avec son écriture : seule la
 * confirmation de CETTE version efface la saisie. Comparer les valeurs ne suffit pas : avec
 * + + - depuis 4, la file contient 5, 6 puis 5, et la confirmation du premier 5 effacerait la
 * saisie alors que le dernier 5 n'est pas encore parti. Pour la même raison, une écriture dont la
 * version n'est plus la dernière sauvegardée n'écrit rien (voir `isObsoleteCountWrite`).
 */

export interface PendingCount {
  /** Valeur telle que tapée ("" = non compté) */
  value: string;
  /** Version de la saisie, propre à chaque frappe */
  rev: string;
  /** Depuis quand l'item a une saisie pas encore confirmée (ms, horloge de l'appareil) */
  since: number;
  /** Dernière frappe (ms) : une saisie toute fraîche est envoyée par la page, pas par le balayage */
  typedAt: number;
  /** Onglet (chargement de page) de la dernière frappe : seule sa page a un debounce en cours */
  typedInTab: string;
  /** Motif d'un refus définitif du serveur (ou valeur invalide) : ne repartira pas tel quel */
  refused?: string;
  /** Statut HTTP du refus (absent pour une valeur invalide) */
  refusedStatus?: number;
  /** `updated_at` de l'inventaire lors du dernier nouvel essai d'un refus 403 (un seul par réouverture) */
  retriedFor?: string;
}

export const INVALID_COUNT_REASON = "Quantité invalide : saisir un nombre entier positif.";

export type PendingCounts = Record<string, PendingCount>;

const KEY_PREFIX = "fidelice:inventory-pending:";
/** Événement émis à chaque modification, pour les composants abonnés dans le même onglet */
const CHANGE_EVENT = "fidelice:inventory-pending-change";

export function pendingCountsKey(inventoryId: string): string {
  return `${KEY_PREFIX}${inventoryId}`;
}

/** Quantité à enregistrer : null si vide, undefined si la valeur n'est pas un entier positif. */
export function parseCountValue(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  // Entier positif uniquement : parseInt("2.5") aurait enregistré 2 en affichant 2.5
  if (!/^\d+$/.test(trimmed)) return undefined;
  return parseInt(trimmed, 10);
}

export function readPendingCounts(inventoryId: string): PendingCounts {
  try {
    const raw = localStorage.getItem(pendingCountsKey(inventoryId));
    return raw ? (JSON.parse(raw) as PendingCounts) : {};
  } catch {
    return {};
  }
}

/** Retourne false si le stockage est indisponible (navigation privée, quota). */
function writePendingCounts(inventoryId: string, counts: PendingCounts): boolean {
  let saved = true;
  try {
    if (Object.keys(counts).length === 0) {
      localStorage.removeItem(pendingCountsKey(inventoryId));
    } else {
      localStorage.setItem(pendingCountsKey(inventoryId), JSON.stringify(counts));
    }
  } catch {
    // L'envoi en mémoire continue de fonctionner, sans survivre à un rechargement
    saved = false;
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
  return saved;
}

/** Inventaires ayant au moins une saisie pas encore confirmée sur cet appareil. */
export function listPendingInventoryIds(): string[] {
  try {
    return Object.keys(localStorage)
      .filter((key) => key.startsWith(KEY_PREFIX))
      .map((key) => key.slice(KEY_PREFIX.length));
  } catch {
    return [];
  }
}

function newRev(): string {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Identifiant de ce chargement de page (change à chaque rechargement) */
export const CURRENT_TAB_ID = newRev();

/**
 * Mémorise une saisie (avant même son envoi) et renvoie sa version, à joindre à l'écriture.
 * Une nouvelle saisie efface un refus précédent et repart de maintenant pour l'alerte d'envoi.
 * Sans stockage, aucune version : l'écriture part alors sans le contrôle d'obsolescence.
 */
export function setPendingCount(
  inventoryId: string,
  itemId: string,
  value: string,
): string | undefined {
  const counts = readPendingCounts(inventoryId);
  const previous = counts[itemId];
  const rev = newRev();
  const now = Date.now();
  counts[itemId] = {
    value,
    rev,
    since: previous && !previous.refused ? previous.since : now,
    typedAt: now,
    typedInTab: CURRENT_TAB_ID,
  };
  return writePendingCounts(inventoryId, counts) ? rev : undefined;
}

/**
 * Écriture dépassée : la saisie a été retapée depuis (version plus récente), confirmée par une
 * autre écriture de même version, ou annulée. L'envoyer écraserait une valeur plus récente,
 * par exemple la reprise tardive d'un envoi lancé depuis un autre onglet.
 */
export function isObsoleteCountWrite(inventoryId: string, itemId: string, rev: string): boolean {
  return readPendingCounts(inventoryId)[itemId]?.rev !== rev;
}

/** La base a confirmé la version `rev` : on oublie la saisie si c'est toujours la dernière. */
export function confirmPendingCount(inventoryId: string, itemId: string, rev: string) {
  const counts = readPendingCounts(inventoryId);
  if (counts[itemId]?.rev !== rev) return;
  delete counts[itemId];
  writePendingCounts(inventoryId, counts);
}

/** Refus définitif de la version `rev` : la saisie est gardée et signalée, sans nouvel envoi. */
export function refusePendingCount(
  inventoryId: string,
  itemId: string,
  rev: string,
  reason: string,
  status?: number,
) {
  const counts = readPendingCounts(inventoryId);
  const entry = counts[itemId];
  if (entry?.rev !== rev) return;
  counts[itemId] = { ...entry, refused: reason, refusedStatus: status };
  writePendingCounts(inventoryId, counts);
}

/**
 * Lève le refus de la version `rev` pour un nouvel essai, mémorisé pour `retriedFor` (l'état de
 * l'inventaire qui le justifie) afin de ne pas boucler. L'alerte d'envoi repart de maintenant.
 */
export function retryRefusedCount(
  inventoryId: string,
  itemId: string,
  rev: string,
  retriedFor: string,
) {
  const counts = readPendingCounts(inventoryId);
  const entry = counts[itemId];
  if (entry?.rev !== rev) return;
  counts[itemId] = {
    value: entry.value,
    rev,
    since: Date.now(),
    typedAt: entry.typedAt,
    typedInTab: entry.typedInTab,
    retriedFor,
  };
  writePendingCounts(inventoryId, counts);
}

/** Oublie les saisies gardées (toutes, ou seulement celles des items indiqués). */
export function discardPendingCounts(inventoryId: string, itemIds?: string[]) {
  if (!itemIds) {
    writePendingCounts(inventoryId, {});
    return;
  }
  const counts = readPendingCounts(inventoryId);
  for (const itemId of itemIds) delete counts[itemId];
  writePendingCounts(inventoryId, counts);
}

/** Abonnement aux modifications (même onglet et autres onglets), pour useSyncExternalStore. */
export function subscribePendingCounts(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/**
 * Attend que toutes les saisies de l'inventaire soient confirmées (« ok »), ou qu'il ne reste
 * que des saisies refusées (« refused »), au plus `timeoutMs` (« waiting » au-delà).
 */
export function waitForPendingCounts(
  inventoryId: string,
  timeoutMs: number,
): Promise<"ok" | "waiting" | "refused"> {
  return new Promise((resolve) => {
    let settled = false;
    const outcome = (): "ok" | "waiting" | "refused" => {
      const entries = Object.values(readPendingCounts(inventoryId));
      if (entries.length === 0) return "ok";
      return entries.every((entry) => entry.refused) ? "refused" : "waiting";
    };
    const finish = (result: "ok" | "waiting" | "refused") => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(result);
    };
    const check = () => {
      const result = outcome();
      if (result !== "waiting") finish(result);
    };
    const timer = setTimeout(() => {
      const entries = Object.values(readPendingCounts(inventoryId));
      finish(entries.some((entry) => entry.refused) ? "refused" : outcome());
    }, Math.max(0, timeoutMs));
    const unsubscribe = subscribePendingCounts(check);
    check();
  });
}
